// src/components/pages/Auth/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";

// ✅ WP auth (WPGraphQL JWT Auth)
import {
  wpFetchMe,
  wpLoginByIdentifier,
  wpLogout,
  wpGetCachedUser,
  wpGetTokens,
  wpHasAuthToken,
  wpGetAuthSession,
  wpIsAuthSessionExpired,
  wpIsCurrentAuthSession,
  WP_AUTH_EVENT,
  wpSetCachedUser,

  // Email OTP APIs
  wpRequestEmailOtp,
  wpVerifyEmailOtp,
} from "../../../api/wpAuth";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => wpGetCachedUser());
  const [loading, setLoading] = useState(true);

  /*
   * Identifies the newest sign-in request started by this provider.
   *
   * A result from an older sign-in attempt must not overwrite the
   * user or loading state of a newer attempt.
   */
  const signInRequestRef = useRef(0);

  /**
   * Bootstrap the authentication state.
   *
   * A valid browser session requires:
   * - an access token;
   * - a refresh token;
   * - a valid session ID;
   * - a fixed, unexpired session deadline;
   * - a valid keep-login mode.
   *
   * Incomplete authentication storage is cleared as one unit.
   */
  useEffect(() => {
    let alive = true;
    let bootstrapSessionId = "";

    (async () => {
      /*
       * Allow the other mounted authentication listeners to register
       * before startup potentially announces an expired session.
       */
      await Promise.resolve();

      if (!alive) return;

      try {
        const tokens =
          wpGetTokens();

        const hasAuthToken =
          wpHasAuthToken();

        const hasRefreshToken =
          Boolean(
            String(
              tokens?.refreshToken || "",
            )
              .replace(
                /^Bearer\s+/i,
                "",
              )
              .trim(),
          );

        const authSession =
          wpGetAuthSession();

        bootstrapSessionId =
          String(
            authSession?.sessionId ||
              "",
          ).trim();

        const hasAnySessionData =
          hasAuthToken ||
          hasRefreshToken ||
          Boolean(authSession);

        /*
         * Completely logged-out browser.
         *
         * Do not restore wp_user by itself because cached profile data
         * is not proof that the user is authenticated.
         */
        if (!hasAnySessionData) {
          if (!alive) return;

          setUser(null);

          wpSetCachedUser(null, {
            reason:
              "bootstrap_no_session",
          });

          return;
        }

        /*
         * A partial session must never be used.
         *
         * Examples:
         * - access token exists but refresh token is missing;
         * - tokens exist but session information is missing;
         * - session information exists but tokens were removed.
         */
        if (
          !hasAuthToken ||
          !hasRefreshToken ||
          !authSession
        ) {
          const cleared =
            wpLogout({
              reason:
                "invalid_session",

              message:
                "Incomplete authentication session.",

              sessionId:
                bootstrapSessionId,
            });

          if (
            alive &&
            cleared !== false
          ) {
            setUser(null);
          }

          return;
        }

        /*
         * Enforce the browser session's fixed 24-hour or 31-day
         * deadline before making an authenticated request.
         */
        if (
          wpIsAuthSessionExpired(
            authSession,
          )
        ) {
          const loggedOut =
            wpLogout({
              reason: "expired",

              message:
                "Session expired. Please login again.",

              sessionId:
                bootstrapSessionId,
            });

          if (
            alive &&
            loggedOut !== false
          ) {
            setUser(null);
          }

          return;
        }

        const me =
          await wpFetchMe({
            debug: false,
          });

        if (!alive) return;

        /*
         * The startup request may finish after the user has logged
         * out or completed a newer login. Never apply its result to
         * a replacement session.
         */
        if (
          !wpIsCurrentAuthSession(
            bootstrapSessionId,
          )
        ) {
          return;
        }

        setUser(me || null);
      } catch {
        if (!alive) return;

        /*
         * Ignore a failure belonging to a session that has already
         * been replaced by a newer login.
         */
        if (
          bootstrapSessionId &&
          !wpIsCurrentAuthSession(
            bootstrapSessionId,
          )
        ) {
          return;
        }

        /*
         * For a temporary non-authentication request failure, retain
         * the cached user instead of falsely presenting it as a
         * deliberate logout.
         *
         * An actual authentication failure is already handled through
         * wpGraphql -> wpLogout, which clears the cache.
         */
        setUser(
          wpGetCachedUser() || null,
        );
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  /**
   * Listen to authentication changes from wpAuth.js.
   *
   * Events carrying a session ID are accepted only when they still
   * belong to the current browser session. This prevents a delayed
   * login, refresh, request, or expiration event from Session A from
   * changing the user state after Session B has already logged in.
   */
  useEffect(() => {
    const onChanged = (event) => {
      const detail =
        event?.detail &&
        typeof event.detail ===
          "object"
          ? event.detail
          : {};

      const eventSessionId =
        String(
          detail?.sessionId || "",
        ).trim();

      const isLogoutEvent =
        detail?.logout === true;

      const currentSession =
        wpGetAuthSession();

      /*
       * An event identifying an older session must not affect the
       * currently active browser session.
       */
      if (
        eventSessionId &&
        currentSession &&
        eventSessionId !==
          currentSession.sessionId
      ) {
        return;
      }

      /*
       * A non-logout event claiming to belong to a session is stale
       * when that session no longer exists in this browser.
       *
       * Logout is allowed here because wpLogout() clears storage
       * before dispatching its event.
       */
      if (
        eventSessionId &&
        !currentSession &&
        !isLogoutEvent
      ) {
        return;
      }

      if (
        eventSessionId &&
        currentSession &&
        !wpIsCurrentAuthSession(
          eventSessionId,
        )
      ) {
        return;
      }

      /*
       * Logout always clears the context user.
       *
       * Stale session-specific logout calls are already rejected
       * inside wpLogout(), but this remains the final UI safeguard.
       */
      if (isLogoutEvent) {
        setUser(null);

        setLoading((current) =>
          current ? false : current,
        );

        return;
      }

      const hasExplicitUser =
        Object.prototype
          .hasOwnProperty.call(
            detail,
            "user",
          );

      const nextUser =
        hasExplicitUser
          ? detail.user
          : wpGetCachedUser();

      setUser(nextUser || null);

      /*
       * Once an authentication event fires, startup loading no longer
       * needs to remain active.
       */
      setLoading((current) =>
        current ? false : current,
      );
    };

    window.addEventListener(
      WP_AUTH_EVENT,
      onChanged,
    );

    return () => {
      window.removeEventListener(
        WP_AUTH_EVENT,
        onChanged,
      );
    };
  }, []);

  /**
   * Sign in using the identifier, password, and current 保持登入
   * selection.
   *
   * Only the newest sign-in attempt may update this context.
   */
  const signIn = useCallback(
    async ({
      identifier,
      password,
      keepLogin = false,
      cc = "+886",
    }) => {
      const signInRequestId =
        ++signInRequestRef.current;

      setLoading(true);

      try {
        const result =
          await wpLoginByIdentifier(
            {
              identifier,
              password,
              cc,
            },
            {
              debug: false,
              keepLogin:
                Boolean(keepLogin),
            },
          );

        /*
         * Another sign-in attempt started while this one was running.
         * Its result must not update the newer attempt's user state.
         */
        if (
          signInRequestId !==
          signInRequestRef.current
        ) {
          throw new Error(
            "Sign-in was replaced by a newer authentication attempt",
          );
        }

        const loginSessionId =
          String(
            result?.login
              ?.sessionId || "",
          ).trim();

        /*
         * wpLogin() returns the session ID supplied by the backend.
         * Confirm that it is still the active browser session before
         * accepting its user result.
         */
        if (
          !loginSessionId ||
          !wpIsCurrentAuthSession(
            loginSessionId,
          )
        ) {
          throw new Error(
            "The completed sign-in no longer belongs to the active session",
          );
        }

        const me =
          result?.me ||
          wpGetCachedUser();

        if (!me?.id) {
          throw new Error(
            "The signed-in user could not be confirmed",
          );
        }

        setUser(me);

        return me;
      } finally {
        /*
         * An older sign-in request must not turn off loading for a
         * newer sign-in attempt that is still running.
         */
        if (
          signInRequestId ===
          signInRequestRef.current
        ) {
          setLoading(false);
        }
      }
    },
    [],
  );

  /**
   * ✅ Email OTP helpers (for AuthPage register / profile email change)
   *
   * purpose:
   * - "register"
   * - "change_email" (requires auth token, wpAuth.js will send it)
   */
  const requestEmailOtp = useCallback(async ({ purpose, email, lang = "ZH" }) => {
    // This should not flip global loading; OTP UI should manage its own local loading.
    return await wpRequestEmailOtp({ purpose, email, lang }, { debug: false });
  }, []);

  const verifyEmailOtp = useCallback(async ({ purpose, email, code }) => {
    return await wpVerifyEmailOtp({ purpose, email, code }, { debug: false });
  }, []);

  const signUp = useCallback(async () => {
    throw new Error(
      "signUp is handled by WP register mutation in AuthPage (sehoRegisterCustomer)."
    );
  }, []);

  const googleLogin = useCallback(async () => {
    throw new Error("googleLogin is not implemented for WP auth yet.");
  }, []);

  /**
   * User-initiated logout clears only the current browser session.
   *
   * It must also invalidate any sign-in request that started before
   * logout so a delayed response cannot restore the removed session.
   */
  const logout = useCallback(async () => {
    /*
     * Make all currently running sign-in attempts stale before
     * clearing the browser's authentication data.
     */
    signInRequestRef.current += 1;

    try {
      wpLogout({
        reason: "user",
      });
    } finally {
      setUser(null);
      setLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      signUp,
      googleLogin,
      logout,

      // ✅ NEW: OTP helpers
      requestEmailOtp,
      verifyEmailOtp,
    }),
    [
      user,
      loading,
      signIn,
      signUp,
      googleLogin,
      logout,
      requestEmailOtp,
      verifyEmailOtp,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider />");
  return ctx;
}
